import { A1CheckoutCartObject } from '@/types/common.ts';
import { useApolloClient } from '@apollo/client';
import { Button } from '@mui/material';
import Dinero from 'dinero.js';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { NUVEI_ENV, NUVEI_SITE_ID } from '../../config';
import {
    AnswersQuestionnaireDocument,
    CreateCheckoutSessionDocument,
    CreateOrderDocument,
    CreateOrderMutationVariables,
    OrderSummaryEventQuery,
    PaymentPageEventQuery,
    QuestionnaireAnswerInput,
} from '../../graphql/graphql.ts';
import { CartContext } from '../providers/cart.tsx';

interface Props {
    event: OrderSummaryEventQuery['event'] | PaymentPageEventQuery['event'];
    chargeAmount: number;
    chargeCurrency: string;
    checkoutNotes?: string;
    setErrorAlert: (err: string) => void;
    cartHold: A1CheckoutCartObject['hold'];
    onContinueClick?: () => void;
    hasQuestionnaire: boolean;
}

export const ApplePayButtonComponent: FC<Props> = ({
    event,
    chargeAmount,
    chargeCurrency,
    setErrorAlert,
    cartHold,
    onContinueClick,
    hasQuestionnaire,
}) => {
    const { t } = useTranslation();
    const [currentSession, setCurrentSession] = useState<ApplePaySession | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const isApplePayEnabled = Boolean(window?.ApplePaySession?.canMakePayments());

    const apollo = useApolloClient();

    const history = useHistory();

    const { cart, setCart, setProcessedOrderId } = useContext(CartContext);

    const chargeDinero = useMemo(
        () => Dinero({ amount: chargeAmount, currency: chargeCurrency as Dinero.Currency }),
        [chargeCurrency, chargeAmount]
    );

    const onPaymentAuthorized =
        (hold: { holdId: string; visitorId: string }) =>
        async (applePayResponse: ApplePayResponse, done: (ok: boolean) => void) => {
            try {
                setIsProcessing(true);

                if (!hold?.holdId || !hold?.visitorId) {
                    console.error('invalid hold');
                    setErrorAlert(t('applePay.errors.sessionExpired', 'Session expired. Please refresh and try again.'));
                    done(false);
                    return;
                }

                if (!applePayResponse?.token?.transactionIdentifier) {
                    setErrorAlert(t('applePay.errors.invalidPaymentToken', 'Invalid payment token. Please try again.'));
                    done(false);
                    return;
                }

                // Add retry logic for session creation
                let sessionCreationResponse;
                let retryCount = 0;
                const maxRetries = 2;

                while (retryCount < maxRetries) {
                    try {
                        sessionCreationResponse = await apollo.mutate({
                            mutation: CreateCheckoutSessionDocument,
                        });

                        if (sessionCreationResponse?.data?.createCheckoutSession?.sessionToken) {
                            break;
                        }
                    } catch (error) {
                        retryCount++;
                        if (retryCount >= maxRetries) {
                            throw error;
                        }
                        // Wait before retry
                        await new Promise((resolve) => setTimeout(resolve, 500));
                    }
                }

                if (!sessionCreationResponse?.data?.createCheckoutSession?.sessionToken) {
                    setErrorAlert(t('applePay.errors.failedToCreateSession', 'Failed to create payment session. Please try again.'));
                    done(false);
                    return;
                }

                const variables: CreateOrderMutationVariables = {
                    payload: {
                        profile: {
                            firstName:
                                applePayResponse.billingContact.givenName ??
                                applePayResponse.shippingContact.givenName,
                            lastName:
                                applePayResponse.billingContact.familyName ??
                                applePayResponse.shippingContact.familyName,
                            email:
                                applePayResponse.billingContact.emailAddress ??
                                applePayResponse.shippingContact.emailAddress,
                            phone: applePayResponse.shippingContact.phoneNumber ?? '',
                            streetAddress:
                                applePayResponse.billingContact.addressLines?.join(', ') ??
                                applePayResponse.shippingContact.addressLines?.join(', ') ??
                                '',
                            city:
                                applePayResponse.billingContact.locality ??
                                applePayResponse.shippingContact.locality ??
                                '',
                            province:
                                applePayResponse.billingContact.administrativeArea ??
                                applePayResponse.shippingContact.administrativeArea ??
                                '',
                            postalCode:
                                applePayResponse.billingContact.postalCode ??
                                applePayResponse.shippingContact.postalCode ??
                                '',
                            country:
                                applePayResponse.billingContact.countryCode ??
                                applePayResponse.shippingContact.countryCode ??
                                'CA',
                        },
                        referral: cart?.referralCodes?.[0] ?? null,
                        paymentRequest: {
                            clientRequestId: uuidv4(),
                            sessionToken:
                                sessionCreationResponse.data.createCheckoutSession.sessionToken,
                            applePayToken: JSON.stringify(applePayResponse.token),
                        },
                        idempotencyKey: applePayResponse.token.transactionIdentifier,
                        hold: {
                            id: hold.holdId,
                            fingerprint: hold.visitorId,
                        },
                    },
                };

                const orderCreationResponse = await apollo.mutate({
                    mutation: CreateOrderDocument,
                    variables,
                });

                if (orderCreationResponse?.data?.createInternalOrder) {
                    setProcessedOrderId(orderCreationResponse.data.createInternalOrder);
                    setCart({ hold: undefined });

                    if (onContinueClick) {
                        void onContinueClick();
                    }

                    if (hasQuestionnaire) {
                        const questionAnswers: QuestionnaireAnswerInput[] =
                            cart?.questionnaireAnswers.map((q) => {
                                return {
                                    question: q.question,
                                    answer: [...q.answer.split(',')],
                                };
                            });

                        const questionnaireResponse = await apollo.mutate({
                            mutation: AnswersQuestionnaireDocument,
                            variables: {
                                orderShortId: orderCreationResponse.data.createInternalOrder,
                                submissionInput: {
                                    questionnaireVersion: event?.questionnaire?.version ?? 1,
                                    answers: questionAnswers,
                                },
                            },
                        });

                        if (
                            !questionnaireResponse.data?.submitQuestionnaireAnswers
                                ?.questionnaireVersion
                        ) {
                            alert(
                                t('applePay.errors.questionnaireSubmissionFailed', 'Payment Successful, questionnaire submission failed. Please contact organizer to complete questionnaire.')
                            );
                        }

                        done(true);

                        history.push('/confirmation');
                    } else {
                        done(true);
                        history.push('/confirmation');
                    }
                } else {
                    done(false);
                }
            } catch (error) {
                console.error('Payment authorization error:', error);

                if (error instanceof Error) {
                    // Check for specific error types
                    if (
                        error.message.includes('precheckout hold') ||
                        error.message.includes('session')
                    ) {
                        setErrorAlert(t('applePay.errors.paymentSessionExpired', 'Payment session expired. Please try again.'));
                    } else {
                        setErrorAlert(error.message);
                    }
                }
                done(false);
            } finally {
                setIsProcessing(false);
                setCurrentSession(null);
            }
        };

    const onClick = useCallback(() => {
        if (isProcessing || !window?.sfc?.applePay || currentSession) {
            return;
        }

        if (chargeDinero.getCurrency() !== 'CAD') {
            return;
        }

        const hold = { holdId: cartHold.id as string, visitorId: cartHold.visitorId as string };

        if (!hold.holdId || !hold.visitorId) {
            setErrorAlert(t('applePay.errors.invalidCartHold', 'Invalid cart hold. Please refresh and try again.'));
            return;
        }

        const config = {
            collectUserDetails: true,
            countryCode: 'CA',
            currencyCode: chargeDinero.getCurrency(),
            supportedNetworks: ['visa', 'masterCard', 'amex'],
            requiredBillingContactFields: ['name', 'postalAddress'],
            requiredShippingContactFields: event?.requirePurchaserAddress
                ? ['name', 'phone', 'email', 'postalAddress']
                : ['name', 'email'],
            total: {
                label: t('applePay.label', 'AdmitONE'),
                amount: chargeDinero.toUnit().toString(),
            },
            merchantSiteId: NUVEI_SITE_ID,
            env: NUVEI_ENV,
            shippingType: 'shipping',
        };

        try {
            const session = window.sfc.applePay.buildSession(config, onPaymentAuthorized(hold));
            setCurrentSession(session);
            session.begin();
        } catch (error) {
            console.error('Failed to create Apple Pay session:', error);
            setErrorAlert(t('applePay.errors.failedToStart', 'Failed to start Apple Pay. Please try again.'));
            setCurrentSession(null);
        }
    }, [onPaymentAuthorized, chargeDinero, cartHold]);

    if (!isApplePayEnabled) {
        return null;
    }

    if (chargeDinero?.getCurrency() !== 'CAD') {
        // We only support CAD for ApplePay at this time
        return null;
    }

    if (chargeDinero.isZero()) {
        return null;
    }

    return (
        <Button
            style={{
                display: 'inline-block',
                // @ts-expect-error CSS directive recognized by Safari browser
                '-webkit-appearance': '-apple-pay-button',
                '-apple-pay-button-type': 'buy',
                height: 48,
            }}
            id="apple-pay-button"
            onClick={onClick}
        />
    );
};
